import { useState, useEffect, useCallback, useRef } from 'react'

const PROXIMITY_THRESHOLD_M = 150 // metres before prompt appears
const SESSION_KEY = 'nearbyEntityPromptDismissed'

/**
 * Haversine distance in metres between two lat/lng pairs.
 */
function haversineMetres(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function getDismissedIds() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]'))
  } catch {
    return new Set()
  }
}

function persistDismissedId(id) {
  try {
    const ids = getDismissedIds()
    ids.add(id)
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...ids]))
  } catch {
    // sessionStorage unavailable — silently skip
  }
}

/**
 * useNearbyEntityPrompt
 *
 * Watches the user's GPS location against the loaded entity list.
 * Builds a queue of ALL entities within PROXIMITY_THRESHOLD_M, sorted
 * nearest-first. Shows the closest non-dismissed entity; calling dismiss()
 * advances to the next in the queue so the user can page through all
 * nearby entities (e.g. at a food truck rally with many trucks close by).
 *
 * @param {{ latitude: number, longitude: number } | null} userLocation
 * @param {string | null} locationSource  — 'geolocation' | 'geocoded' | null
 * @param {Array} entities                — entity objects from the map
 * @returns {{ nearbyEntity, nearbyCount, queueIndex, dismiss }}
 *   nearbyCount — total nearby entities (including already-dismissed ones)
 *   queueIndex  — 1-based position of the current entity in the full nearby set
 */
export default function useNearbyEntityPrompt(userLocation, locationSource, entities) {
  // Full sorted list of nearby entities (rebuilt when location/entities change)
  const [nearbyQueue, setNearbyQueue] = useState([])
  // Index into nearbyQueue of the currently shown entity
  const [queuePos, setQueuePos] = useState(0)
  const prevQueueKey = useRef('')

  useEffect(() => {
    if (locationSource !== 'geolocation') {
      setNearbyQueue([])
      setQueuePos(0)
      return
    }
    if (!userLocation?.latitude || !userLocation?.longitude || !entities?.length) {
      setNearbyQueue([])
      setQueuePos(0)
      return
    }

    const FRESHNESS_MS = 48 * 60 * 60 * 1000 // 48-hour window matches confirmation eligibility
    const now = Date.now()
    const candidates = []
    for (const entity of entities) {
      const eLat = entity.currentLatitude
      const eLng = entity.currentLongitude
      if (!eLat || !eLng) continue
      // Only surface entities with a sighting in the last 48 hours — stale entities
      // have nothing the community can meaningfully confirm right now.
      const seenAt = entity.lastSeenAt || entity.last_seen_at
      if (!seenAt || now - new Date(seenAt).getTime() > FRESHNESS_MS) continue
      const dist = haversineMetres(userLocation.latitude, userLocation.longitude, eLat, eLng)
      if (dist < PROXIMITY_THRESHOLD_M) {
        candidates.push({ entity, dist })
      }
    }
    candidates.sort((a, b) => a.dist - b.dist)
    const sorted = candidates.map((c) => c.entity)

    // Build a stable key to detect meaningful changes (entity IDs + order)
    const key = sorted.map((e) => e.entityId || e.id).join(',')
    if (key === prevQueueKey.current) return
    prevQueueKey.current = key

    // Find the first non-dismissed entity to start from
    const dismissed = getDismissedIds()
    const firstUndismissed = sorted.findIndex((e) => !dismissed.has(e.entityId || e.id))
    setNearbyQueue(sorted)
    setQueuePos(firstUndismissed === -1 ? sorted.length : firstUndismissed)
  }, [userLocation, locationSource, entities])

  const dismiss = useCallback((entityId) => {
    persistDismissedId(entityId)
    // Advance to the next entity in the queue
    setQueuePos((pos) => pos + 1)
  }, [])

  const dismissed = getDismissedIds()
  // Filter the queue to only undismissed entries from queuePos onward
  const remaining = nearbyQueue.slice(queuePos).filter((e) => !dismissed.has(e.entityId || e.id))
  const nearbyEntity = remaining[0] || null
  // 1-based position in the full nearby set (so the UI can show "2 of 5")
  const currentIndex = nearbyEntity
    ? nearbyQueue.findIndex((e) => (e.entityId || e.id) === (nearbyEntity.entityId || nearbyEntity.id))
    : -1

  return {
    nearbyEntity,
    nearbyCount: nearbyQueue.length,
    queueIndex: currentIndex + 1, // 1-based; 0 when no entity
    dismiss,
  }
}
